'use client';

import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  rating: number;
  maxStars?: number;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onChange?: (rating: number) => void;
  showNumeric?: boolean;
  className?: string;
}

const sizeMap = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

const textSizeMap = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

const gapMap = {
  sm: 'gap-0.5',
  md: 'gap-0.5',
  lg: 'gap-1',
};

function StarRating({
  rating,
  maxStars = 5,
  size = 'md',
  interactive = false,
  onChange,
  showNumeric = true,
  className,
}: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const displayRating = hoverRating !== null ? hoverRating : rating;

  const handleClick = (starIndex: number) => {
    if (interactive && onChange) {
      onChange(starIndex);
    }
  };

  const handleMouseEnter = (starIndex: number) => {
    if (interactive) {
      setHoverRating(starIndex);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(null);
    }
  };

  const renderStar = (index: number) => {
    const starNumber = index + 1;
    const filled = displayRating >= starNumber;
    const halfFilled = !filled && displayRating >= starNumber - 0.5;

    return (
      <button
        key={index}
        type="button"
        disabled={!interactive}
        onClick={() => handleClick(starNumber)}
        onMouseEnter={() => handleMouseEnter(starNumber)}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'relative focus:outline-none transition-transform',
          interactive && 'cursor-pointer hover:scale-110',
          !interactive && 'cursor-default'
        )}
        aria-label={`${starNumber} star${starNumber !== 1 ? 's' : ''}`}
      >
        {/* Background (empty) star */}
        <Star
          className={cn(
            sizeMap[size],
            'text-gray-300 dark:text-gray-600'
          )}
        />
        {/* Filled overlay */}
        {(filled || halfFilled) && (
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: filled ? '100%' : '50%' }}
          >
            <Star
              className={cn(
                sizeMap[size],
                'text-amber-400 fill-amber-400'
              )}
            />
          </div>
        )}
      </button>
    );
  };

  return (
    <div className={cn('flex items-center', gapMap[size], className)}>
      <div className={cn('flex items-center', gapMap[size])}>
        {Array.from({ length: maxStars }, (_, i) => renderStar(i))}
      </div>
      {showNumeric && (
        <span
          className={cn(
            'ml-1.5 font-semibold text-gray-900 dark:text-white',
            textSizeMap[size]
          )}
        >
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
}

export { StarRating };
export type { StarRatingProps };
